<script lang="ts">
	// Encapsulates all home-background rendering logic for the new layout (MainLayoutNew):
	//  - resolving the home's photo/color/gradient backgroundColor to an image (see
	//    src/helpers/account_helpers.ts / background_color_helpers.ts)
	//  - applying a per-dashboard backgroundColor override when on /dash/:id (same solid/
	//    gradient token set as the home picker; falls back to the home background when unset)
	//  - cross-fading smoothly between background images (dashboard → dashboard, home →
	//    dashboard, etc.) via stacked layers rendered here, instead of MainLayout's own
	//    inline `background-image` which would swap instantly. MainLayout is therefore told
	//    NOT to paint the image (we pass an empty `backgroundImgSrc`); it keeps rendering the
	//    legacy solid-color class path (`bg-*`) for home colors that have no pre-rendered image.
	//  - applying the shared overlay opacity on top of it (see --home-background-overlay-opacity
	//    in src/app.css)
	//  - overriding the background with a solid color, with a smooth transition, on routes
	//    configured in src/helpers/route_background_helpers.ts (e.g. all /settings routes)
	//
	// Usage - wrap the design system <MainLayout> and forward the slot props to it:
	//   <HomeBackground let:backgroundImgSrc let:backgroundColor>
	//     <MainLayout {backgroundImgSrc} {backgroundColor} ...>...</MainLayout>
	//   </HomeBackground>
	import { onDestroy } from 'svelte';
	import { location } from 'svelte-spa-router';
	import { profile_data } from '../stores';
	import { dashboards } from '../stores/features/dashboards';
	import { getAppBackgroundSrc, hasBackgroundColorImage } from '../helpers/account_helpers';
	import { getBackgroundColorImage } from '../helpers/background_color_helpers';
	import { load_image_from } from '../helpers/interface_helpers';
	import { getRouteBackgroundOverrideColor } from '../helpers/route_background_helpers';

	const getDashIdFromLocation = (loc: string): string | null => {
		const parts = loc.split('/').filter(Boolean);
		if (parts[0] !== 'dash' || !parts[1]) return null;
		return parts[1];
	};

	$: homeBackgroundImgSrc = getAppBackgroundSrc($profile_data, true);
	$: homeHasBgImage = hasBackgroundColorImage($profile_data);
	$: homeBackgroundColor = homeHasBgImage
		? undefined
		: $profile_data?.layout?.backgroundColor || undefined;

	$: dashId = getDashIdFromLocation($location);
	$: dashBackgroundToken = dashId ? $dashboards[dashId]?.backgroundColor?.trim() : '';
	$: dashBackgroundImage = getBackgroundColorImage(dashBackgroundToken);

	$: backgroundImgSrc = dashBackgroundImage
		? load_image_from(dashBackgroundImage.full)
		: homeBackgroundImgSrc;
	// Only the legacy home solid-color path (a token with no pre-rendered image) still uses
	// MainLayout's `bg-*` class; every other case resolves to a cross-faded image below.
	$: backgroundColor = dashBackgroundImage ? undefined : dashBackgroundToken || homeBackgroundColor;
	$: routeOverrideColor = getRouteBackgroundOverrideColor($location);

	// --- Image cross-fade -------------------------------------------------------------------
	// Keep the CSS duration and this JS prune delay in sync (see --home-background-transition-
	// duration in src/app.css). The image the cross-fade should show is empty whenever the
	// color-class path is active, so those transitions fall back to MainLayout's overlay fade.
	const TRANSITION_MS = 350;

	type BgLayer = { id: number; src: string; on: boolean };
	let bgLayers: BgLayer[] = [];
	let bgLayerSeq = 0;
	let currentBgSrc: string | null = null;
	let pruneTimer: ReturnType<typeof setTimeout> | null = null;

	$: crossfadeSrc = backgroundColor ? '' : backgroundImgSrc || '';
	$: syncBgLayers(crossfadeSrc);

	function syncBgLayers(src: string) {
		if (src === currentBgSrc) return;
		currentBgSrc = src;

		// No image to show (color-class path / empty): fade the current layers out.
		if (!src) {
			bgLayers = bgLayers.map((l) => ({ ...l, on: false }));
			return;
		}

		const isFirst = bgLayers.length === 0;
		const id = ++bgLayerSeq;

		// Drop any already-hidden layers, then stack the new one. The first-ever layer
		// starts visible to avoid an initial fade-in from black on app load.
		bgLayers = [...bgLayers.filter((l) => l.on), { id, src, on: isFirst }];

		if (!isFirst) {
			// Two RAFs so the browser paints the new layer at opacity 0 before we flip it on.
			requestAnimationFrame(() => {
				requestAnimationFrame(() => {
					bgLayers = bgLayers.map((l) => ({ ...l, on: l.id === id }));
				});
			});
		}

		if (pruneTimer) clearTimeout(pruneTimer);
		pruneTimer = setTimeout(() => {
			bgLayers = bgLayers.filter((l) => l.on);
		}, TRANSITION_MS + 50);
	}

	onDestroy(() => {
		if (pruneTimer) clearTimeout(pruneTimer);
	});
</script>

<div
	class="home-background-overlay"
	class:home-background-route-override={!!routeOverrideColor}
	style={routeOverrideColor ? `--home-background-route-override-color: ${routeOverrideColor};` : ''}
>
	<div class="bg-crossfade" aria-hidden="true">
		{#each bgLayers as layer (layer.id)}
			<div
				class="bg-layer"
				class:on={layer.on}
				style={`background-image: url("${layer.src}");`}
			></div>
		{/each}
	</div>
	<slot backgroundImgSrc="" {backgroundColor} />
</div>

<style>
	/*
	 * All home backgrounds (photos, solid colors, and gradients) render with the lighter
	 * overlay opacity defined once by `--home-background-overlay-opacity` (src/app.css),
	 * instead of the design system's default 80% dark overlay. The override is scoped to only
	 * the MainLayout background overlay (`.main-layout::before`) and explicitly reset on its
	 * content children so it never leaks into unrelated overlays that also read this variable
	 * (Modal, Drawer, Sidepanel, Dialog, etc.).
	 */
	.home-background-overlay {
		--background-image-overlay: rgba(0, 0, 0, var(--home-background-overlay-opacity));
	}
	.home-background-overlay :global(.main-layout > *) {
		--background-image-overlay: rgba(0, 0, 0, 0.8);
	}

	/*
	 * Cross-fade layers sit behind everything (MainLayout paints on top with a transparent
	 * background, so these show through and its `::before` overlay still darkens them). Each
	 * layer fades its opacity when a new background image is set, giving a smooth image →
	 * image transition without passing through the black route-override overlay.
	 */
	.bg-crossfade {
		position: fixed;
		inset: 0;
		z-index: -1;
		overflow: hidden;
		pointer-events: none;
	}
	.bg-layer {
		position: absolute;
		inset: 0;
		background-size: cover;
		background-position: center;
		background-repeat: no-repeat;
		opacity: 0;
		transition: opacity var(--home-background-transition-duration) ease;
	}
	.bg-layer.on {
		opacity: 1;
	}

	/*
	 * Smoothly cross-fades the MainLayout background overlay whenever it changes (normal
	 * overlay <-> route override), instead of an abrupt cut. Since `.main-layout::before` is
	 * painted behind the header/content (see design system MainLayout.svelte), fading it to a
	 * fully opaque color visually replaces the photo/color/gradient underneath with that color.
	 */
	.home-background-overlay :global(.main-layout::before) {
		transition: background var(--home-background-transition-duration) ease;
	}

	/* Route-specific override (see src/helpers/route_background_helpers.ts) - takes priority
	   over the normal overlay above by being declared after it, same specificity. */
	.home-background-route-override {
		--background-image-overlay: var(--home-background-route-override-color);
	}
</style>
